import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const csv = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");
const fromCsv = (t) => (t || "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);

export default function DocumentTaxonomy() {
  const { user } = useAuth();
  const canGovern = ["administrator", "partner"].includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [published, setPublished] = useState(null);
  const [draft, setDraft] = useState(null);
  const [groups, setGroups] = useState([]);
  const [versions, setVersions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [rows, setRows] = useState([]);   // editable draft classes
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState({ code: "", label: "", group: "support", rationale: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/document-taxonomy");
      setPublished(data.published || null);
      setDraft(data.draft || null);
      setGroups(data.groups || []);
      setRows(JSON.parse(JSON.stringify((data.draft || {}).classes || [])));
      const [v, a, p] = await Promise.all([
        api.get("/document-taxonomy/versions"), api.get("/document-taxonomy/audit"), api.get("/document-taxonomy/proposals"),
      ]);
      setVersions(v.data.versions || []); setAudit(a.data.audit || []); setProposals(p.data.proposals || []);
    } catch (e) { toast.error("Failed to load taxonomy."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const groupLabel = useMemo(() => Object.fromEntries(groups), [groups]);
  const publishedClasses = published?.classes || [];
  const byGroup = useMemo(() => {
    const m = {}; (rows.length ? rows : publishedClasses).forEach((c) => { (m[c.group] = m[c.group] || []).push(c); }); return m;
  }, [rows, publishedClasses]);

  const startDraft = async () => { setBusy(true); try { const { data } = await api.post("/document-taxonomy/draft/start"); setDraft(data); setRows(JSON.parse(JSON.stringify(data.classes || []))); toast.success("Draft started from published."); } catch { toast.error("Could not start draft."); } finally { setBusy(false); } };
  const saveDraft = async () => { setBusy(true); try { const { data } = await api.put("/document-taxonomy/draft", { classes: rows }); setDraft(data); toast.success("Draft saved."); load(); } catch { toast.error("Save failed."); } finally { setBusy(false); } };
  const approve = async () => { setBusy(true); try { await api.post("/document-taxonomy/draft/approve", {}); toast.success("Draft approved."); load(); } catch (e) { toast.error(e.response?.data?.detail || "Approve failed."); } finally { setBusy(false); } };
  const publish = async () => { setBusy(true); try { const { data } = await api.post("/document-taxonomy/draft/publish", {}); toast.success(`Published v${data.version}.`); setDraft(null); load(); } catch (e) { toast.error(e.response?.data?.detail || "Publish failed."); } finally { setBusy(false); } };
  const discard = async () => { setBusy(true); try { await api.post("/document-taxonomy/draft/discard"); setDraft(null); setRows([]); toast.success("Draft discarded."); load(); } catch (e) { toast.error(e.response?.data?.detail || "Discard failed."); } finally { setBusy(false); } };
  const restore = async (id) => { setBusy(true); try { await api.post(`/document-taxonomy/versions/${id}/restore`); toast.success("Restored to a new draft."); load(); } catch (e) { toast.error(e.response?.data?.detail || "Restore failed."); } finally { setBusy(false); } };
  const submitProposal = async () => { if (!proposal.label) return toast.error("Add a class name."); setBusy(true); try { await api.post("/document-taxonomy/proposals", proposal); toast.success("Proposal submitted for Partner/Admin review."); setProposal({ code: "", label: "", group: "support", rationale: "" }); load(); } catch { toast.error("Could not submit."); } finally { setBusy(false); } };
  const resolveProposal = async (id, status) => { try { await api.post(`/document-taxonomy/proposals/${id}/resolve`, { status }); toast.success(`Proposal ${status}.`); load(); } catch { toast.error("Failed."); } };

  const setRow = (i, patch) => setRows((r) => r.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const addRow = () => setRows((r) => [...r, { code: "", label: "", group: "support", jurisdictions: [], aliases: [], active: true }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  if (loading) return <div className="p-6 text-slate-500" data-testid="taxonomy-loading">Loading taxonomy…</div>;

  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-5" data-testid="document-taxonomy-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2"><Icons.Tags className="h-6 w-6 text-blue-600" />Document Taxonomy</h1>
          <p className="text-sm text-slate-500 mt-1">Governed institutional knowledge — the canonical document vocabulary Allison and the Document Intelligence Package consume. Partner/Admin governed via Draft → Review → Publish.</p>
        </div>
        <div className="text-right shrink-0">
          <Badge className="bg-emerald-100 text-emerald-700" data-testid="taxonomy-published-version">Published v{published?.version ?? "—"}</Badge>
          {draft && <div className="mt-1"><Badge className="bg-amber-100 text-amber-700" data-testid="taxonomy-draft-badge">Draft {draft.approved_by ? "· approved" : "· needs approval"}</Badge></div>}
        </div>
      </div>

      <Tabs defaultValue="classes">
        <TabsList data-testid="taxonomy-tabs">
          <TabsTrigger value="classes" data-testid="tab-classes">Classes</TabsTrigger>
          <TabsTrigger value="versions" data-testid="tab-versions">Versions</TabsTrigger>
          <TabsTrigger value="proposals" data-testid="tab-proposals">Proposals</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
        </TabsList>

        {/* CLASSES */}
        <TabsContent value="classes" className="space-y-4">
          {canGovern ? (
            <div className="flex items-center gap-2 flex-wrap" data-testid="taxonomy-governance-bar">
              {!draft ? (
                <Button onClick={startDraft} disabled={busy} data-testid="taxonomy-start-draft"><Icons.FilePlus2 className="h-4 w-4 mr-1.5" />Start Draft</Button>
              ) : (
                <>
                  <Button onClick={saveDraft} disabled={busy} data-testid="taxonomy-save-draft"><Icons.Save className="h-4 w-4 mr-1.5" />Save Draft</Button>
                  <Button variant="outline" onClick={approve} disabled={busy || !!draft.approved_by} data-testid="taxonomy-approve"><Icons.CheckCheck className="h-4 w-4 mr-1.5" />Approve</Button>
                  <Button onClick={publish} disabled={busy || !draft.approved_by} className="bg-emerald-600 hover:bg-emerald-700" data-testid="taxonomy-publish"><Icons.UploadCloud className="h-4 w-4 mr-1.5" />Publish</Button>
                  <Button variant="ghost" onClick={discard} disabled={busy} className="text-rose-600" data-testid="taxonomy-discard"><Icons.Trash2 className="h-4 w-4 mr-1.5" />Discard</Button>
                </>
              )}
            </div>
          ) : (
            <div className="text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid="taxonomy-readonly-note">You can view the taxonomy and submit proposals. Only Partner/Admin can edit and publish.</div>
          )}

          {draft && canGovern ? (
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="taxonomy-editor">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
                  <th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Label</th><th className="text-left px-3 py-2">Group</th><th className="text-left px-3 py-2">Aliases</th><th className="text-left px-3 py-2">Jurisdictions</th><th className="px-3 py-2">Active</th><th></th>
                </tr></thead>
                <tbody>
                  {rows.map((c, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`taxonomy-row-${i}`}>
                      <td className="px-2 py-1"><Input value={c.code} onChange={(e) => setRow(i, { code: e.target.value })} className="h-8 text-xs" placeholder="code" /></td>
                      <td className="px-2 py-1"><Input value={c.label} onChange={(e) => setRow(i, { label: e.target.value })} className="h-8 text-xs" placeholder="Label" /></td>
                      <td className="px-2 py-1">
                        <select value={c.group} onChange={(e) => setRow(i, { group: e.target.value })} className="h-8 text-xs border border-slate-200 rounded px-1 w-full">
                          {groups.map(([g, l]) => <option key={g} value={g}>{l}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1"><Input value={csv(c.aliases)} onChange={(e) => setRow(i, { aliases: fromCsv(e.target.value) })} className="h-8 text-xs" placeholder="alias, alias" /></td>
                      <td className="px-2 py-1"><Input value={csv(c.jurisdictions)} onChange={(e) => setRow(i, { jurisdictions: fromCsv(e.target.value) })} className="h-8 text-xs" placeholder="us, uk" /></td>
                      <td className="px-2 py-1 text-center"><input type="checkbox" checked={!!c.active} onChange={(e) => setRow(i, { active: e.target.checked })} data-testid={`taxonomy-active-${i}`} /></td>
                      <td className="px-2 py-1"><button onClick={() => removeRow(i)} className="text-slate-400 hover:text-rose-600" data-testid={`taxonomy-remove-${i}`}><Icons.X className="h-4 w-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-2 border-t border-slate-100"><Button variant="outline" size="sm" onClick={addRow} data-testid="taxonomy-add-class"><Icons.Plus className="h-4 w-4 mr-1" />Add Class</Button></div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="taxonomy-published-view">
              {groups.map(([g, l]) => (byGroup[g]?.length ? (
                <div key={g} className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{l}</div>
                  <div className="flex flex-wrap gap-2">
                    {byGroup[g].map((c) => (
                      <span key={c.code} data-testid={`taxonomy-class-${c.code}`} className={`inline-flex items-center gap-1.5 text-xs rounded-full border px-2.5 py-1 ${c.active ? "border-slate-200 text-slate-700" : "border-slate-100 text-slate-400 line-through"}`} title={(c.aliases || []).join(", ")}>
                        {c.label}{c.jurisdictions?.length ? <span className="text-[10px] text-slate-400 uppercase">{c.jurisdictions.join("/")}</span> : null}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null))}
            </div>
          )}
        </TabsContent>

        {/* VERSIONS */}
        <TabsContent value="versions" className="space-y-2">
          {versions.map((v) => (
            <div key={v.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm" data-testid={`taxonomy-version-${v.version || 'draft'}`}>
              <div className="flex items-center gap-3">
                <Badge className={v.status === "published" ? "bg-emerald-100 text-emerald-700" : v.status === "archived" ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-700"}>{v.status === "published" ? `v${v.version} · Published` : v.status === "archived" ? `v${v.version} · Archived` : "Draft"}</Badge>
                <span className="text-slate-500 text-xs">{(v.classes || []).length} classes · {v.published_by || v.updated_by || "—"}</span>
              </div>
              {canGovern && v.status === "archived" && <Button size="sm" variant="outline" onClick={() => restore(v.id)} data-testid={`taxonomy-restore-${v.version}`}><Icons.RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</Button>}
            </div>
          ))}
        </TabsContent>

        {/* PROPOSALS */}
        <TabsContent value="proposals" className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid="taxonomy-proposal-form">
            <div className="text-sm font-medium text-slate-700">Propose a document class</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input placeholder="Class name (e.g. P60)" value={proposal.label} onChange={(e) => setProposal({ ...proposal, label: e.target.value })} data-testid="proposal-label" />
              <select value={proposal.group} onChange={(e) => setProposal({ ...proposal, group: e.target.value })} className="border border-slate-200 rounded px-2 text-sm" data-testid="proposal-group">
                {groups.map(([g, l]) => <option key={g} value={g}>{l}</option>)}
              </select>
              <Button onClick={submitProposal} disabled={busy} data-testid="proposal-submit"><Icons.Send className="h-4 w-4 mr-1.5" />Submit</Button>
            </div>
            <Input placeholder="Why is this class needed?" value={proposal.rationale} onChange={(e) => setProposal({ ...proposal, rationale: e.target.value })} data-testid="proposal-rationale" />
          </div>
          {proposals.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm" data-testid={`proposal-${p.id}`}>
              <div><span className="font-medium text-slate-800">{p.label}</span> <span className="text-xs text-slate-500">· {groupLabel[p.group] || p.group} · by {p.submitted_by}</span>{p.rationale ? <div className="text-xs text-slate-500 mt-0.5">{p.rationale}</div> : null}</div>
              <div className="flex items-center gap-2">
                <Badge className={p.status === "open" ? "bg-amber-100 text-amber-700" : p.status === "accepted" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>{p.status}</Badge>
                {canGovern && p.status === "open" && (<>
                  <button onClick={() => resolveProposal(p.id, "accepted")} className="text-emerald-600 text-xs hover:underline" data-testid={`proposal-accept-${p.id}`}>Accept</button>
                  <button onClick={() => resolveProposal(p.id, "rejected")} className="text-rose-600 text-xs hover:underline" data-testid={`proposal-reject-${p.id}`}>Reject</button>
                </>)}
              </div>
            </div>
          ))}
        </TabsContent>

        {/* AUDIT */}
        <TabsContent value="audit" className="space-y-1.5">
          {audit.map((a) => (
            <div key={a.id} className="flex items-center gap-3 text-xs text-slate-600 border-b border-slate-50 py-1.5" data-testid={`taxonomy-audit-${a.id}`}>
              <Icons.History className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-medium text-slate-700">{a.event}</span>
              <span className="text-slate-400">v{String(a.version)}</span>
              <span className="text-slate-400">· {a.actor}</span>
              {a.notes ? <span className="text-slate-400">· {a.notes}</span> : null}
              <span className="ml-auto text-slate-300">{new Date(a.at).toLocaleString()}</span>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
